import { jsx as _jsx, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Label, Tooltip, TooltipPortal } from "@animech/ui";
import { LayerSwitch } from "../layer-switch/layer-switch";
import * as Styled from "./pallet-preset-switch.style";
import { PalletPatternIcon } from "./pallet-pattern-icon";
import { SavedPalletLayouts } from "./saved-pallet-layouts";
import { useAppDispatch, useAppSelector, actions, selectors } from "store";
import { arePlacementsEquivalent, computePalletPattern, computePatternPreview, getPalletPresets, } from "3d/pallet-patterns";
import { getBoxWorldFootprint } from "3d/pallet";
import { objectPosition } from "3d/constants/common";
import { PatternTransform } from "store/types";
import { LockIcon } from "ui/components/elements/icons/lock";
import { useNumberConversionWithMillimeters } from "hooks/number-conversion";
import { Unit } from "utility/number";
/*
 * Picks the palletizing pattern the active layer of boxes is arranged in. Each
 * preset shows a top view of what it does to the current pallet and how many
 * boxes it ends up with — the box count is a result of the pattern and the box
 * size, not something the user sets, so it has to be visible before choosing.
 *
 * A pattern's mirrored/turned layouts are separate presets right below the
 * pattern itself, and only appear when they fit the pallet and actually land
 * differently — so everything on offer is applicable, and layers can be made
 * to interlock (real stacks alternate layouts so they don't fall over) by
 * picking a variant, with no extra controls.
 *
 * A pattern that does nothing of its own on this pallet (a pinwheel with no
 * room for a group, a brick with one box per row) is locked rather than hidden:
 * it shows what the pattern looks like in principle, and its tooltip says what
 * it would take — usually a larger pallet, which the user would not otherwise
 * think of. See `getPalletPresets`.
 *
 * Nothing is highlighted when the layer has been arranged by hand ("custom"):
 * picking a preset then takes it back over. Rendered only in the cardboard
 * application (see canvas.tsx).
 */
export var PalletPresetSwitch = function () {
    var t = useTranslation().t;
    var dispatch = useAppDispatch();
    var objectSize = useAppSelector(selectors.selectObjectSize);
    var pallet = useAppSelector(selectors.selectPallet);
    var layerPatterns = useAppSelector(selectors.selectLayerPatterns);
    var activeLayer = useAppSelector(selectors.selectActiveLayer);
    /* The parameter drawers and the in-canvas cup drawers all open over the
       canvas from its left edge, right where this control sits, and every one of
       them is driven by `activeSidebarItemID` — so that alone says whether
       anything covers the control. Deliberately NOT `activeMenuItem`: that only
       says which sidebar item is expanded, and selecting a cup expands
       `MenuItem.SELECTED_ENTITY` inside the sidebar, covering nothing. */
    var activeSidebarItemID = useAppSelector(selectors.selectActiveSidebarItemID);
    var activePattern = layerPatterns[activeLayer];
    var boxFootprint = useMemo(function () { return getBoxWorldFootprint(objectSize); }, [objectSize]);
    var presets = useMemo(function () { return getPalletPresets(boxFootprint, pallet, objectPosition); }, [boxFootprint, pallet]);
    /* The active layer's entry in the offered list. An exact transform match
       first; failing that, the entry that lands the same layout — a stored
       transform whose variant is suppressed as a lookalike (e.g. a mirrored
       block from an older save) still highlights the pattern it repeats. */
    var selectedPreset = useMemo(function () {
        var _a;
        if (!activePattern)
            return null;
        var candidates = presets.filter(function (entry) { return entry.patternId === activePattern.patternId; });
        var exact = candidates.find(function (entry) { return entry.transform === activePattern.transform; });
        if (exact)
            return exact;
        var activePlacements = computePalletPattern(activePattern.patternId, boxFootprint, pallet, objectPosition, activePattern.transform);
        return ((_a = candidates.find(function (entry) {
            return arePlacementsEquivalent(entry.placements, activePlacements, boxFootprint);
        })) !== null && _a !== void 0 ? _a : null);
    }, [activePattern, boxFootprint, pallet, presets]);
    var numberConverter = useNumberConversionWithMillimeters();
    var presetName = function (entry) {
        var name = t("pallet_presets.patterns.".concat(entry.patternId));
        return entry.transform === PatternTransform.NONE
            ? name
            : "".concat(name, " (").concat(t("pallet_presets.variants.".concat(entry.transform)), ")");
    };
    var tooltipText = function (entry) {
        var name = presetName(entry);
        var lock = entry.lock;
        if (!lock) {
            return t("pallet_presets.tooltip", {
                name: name,
                count: entry.placements.length,
            });
        }
        switch (lock.reason) {
            case "pallet_too_small": {
                var length = numberConverter(lock.minimumPallet.length, Unit.M);
                var width = numberConverter(lock.minimumPallet.width, Unit.M);
                return t("pallet_presets.locked.pallet_too_small", {
                    name: name,
                    length: length.value,
                    width: width.value,
                    unit: width.unit,
                });
            }
            case "same_as":
                return t("pallet_presets.locked.same_as", {
                    name: name,
                    other: t("pallet_presets.patterns.".concat(lock.patternId)),
                });
            case "does_not_fit":
            default:
                return t("pallet_presets.locked.does_not_fit", { name: name });
        }
    };
    return (_jsxs(Styled.PalletPresetSwitch, { isDrawerOpen: activeSidebarItemID !== undefined, 
        /* The canvas picker listens for mouse-down; without this, using the
           control would also deselect or start dragging in the scene. */
        onMouseDown: function (event) { return event.stopPropagation(); }, onWheel: function (event) { return event.stopPropagation(); }, children: [_jsx(LayerSwitch, {}), _jsx(Label, { size: "small", children: t("pallet_presets.heading") }), _jsx(Styled.PresetList, { children: presets.map(function (entry) {
                    var name = presetName(entry);
                    var isSelected = entry === selectedPreset;
                    var isLocked = !!entry.lock;
                    /* A locked preset's own layout is the one-box fallback or a
                       lookalike — it shows the pattern in principle instead. */
                    var icon = isLocked
                        ? computePatternPreview(entry.patternId, pallet, objectPosition)
                        : { boxFootprint: boxFootprint, placements: entry.placements };
                    return (_jsx(TooltipPortal, { anchorElement: _jsxs(Styled.PresetButton, { isSelected: isSelected, isLocked: isLocked, "aria-disabled": isLocked, onClick: function () {
                                if (isLocked)
                                    return;
                                dispatch(actions.applyPalletPattern(entry.patternId, entry.transform));
                            }, "aria-label": name, "aria-pressed": isSelected, children: [_jsx(PalletPatternIcon, { pallet: pallet, boxFootprint: icon.boxFootprint, placements: icon.placements }), isLocked ? (_jsx(LockIcon, { size: "10px", className: "".concat(Styled.prefix, "-lock") })) : (_jsx("span", { className: "".concat(Styled.prefix, "-box-count"), children: entry.placements.length }))] }), toggleEvent: "hover", placement: "right", children: _jsx(Tooltip, { color: "neutral", children: tooltipText(entry) }) }, "".concat(entry.patternId, "-").concat(entry.transform)));
                }) }), _jsx(SavedPalletLayouts, {})] }));
};
